import { useCallback, useEffect, useState } from 'react';
import { useApiFetch } from '../api';
import type { ApiResponseDomainOperationPrices } from '../types/api/ApiResponseDomainPricing';

/** One resolved cell for the in-scope client: the effective price + whether it's a client override. */
export interface ClientDomainCell {
  unit_price: string;
  is_custom: boolean;
}

interface UseClientDomainPricingReturn {
  /** Effective cells keyed by `${slug}:${tld}`. A missing key = no override and no default (unpriced). */
  priceMap: Map<string, ClientDomainCell>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Upsert (or remove, when cell is null) one cell in place from a PUT/DELETE response. */
  applyClientCell: (slug: string, tld: string, cell: ClientDomainCell | null) => void;
}

const cellKey = (slug: string, tld: string) => `${slug}:${tld}`;

// Builds the in-scope (impersonated) client's domain pricing matrix by fetching GET /prices/domains/{slug}
// for each operation in parallel and merging the effective cells. useApiFetch attaches the
// X-Impersonate-Client-ID header, so these resolve to the impersonated client; the effect re-runs when
// that target changes (apiFetch identity changes with it). `enabled` defers until the tab is shown and
// a client is impersonated; while disabled the map is cleared so a stale client's prices never linger.
export const useClientDomainPricing = (slugs: string[], enabled: boolean): UseClientDomainPricingReturn => {
  const [priceMap, setPriceMap] = useState<Map<string, ClientDomainCell>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiFetch = useApiFetch();

  const fetchAll = useCallback(async () => {
    if (slugs.length === 0) {
      setPriceMap(new Map());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const perOperation = await Promise.all(
        slugs.map(async (slug) => {
          const response = await apiFetch(`/prices/domains/${slug}`);
          const body = (await response.json().catch(() => null)) as ApiResponseDomainOperationPrices | null;
          if (body?.status === 'success' && body.data?.prices) {
            return { slug, prices: body.data.prices };
          }
          throw new Error(body?.message ?? `Failed to load prices for "${slug}"`);
        }),
      );
      const map = new Map<string, ClientDomainCell>();
      perOperation.forEach(({ slug, prices }) => {
        prices.forEach((p) => map.set(cellKey(slug, p.tld), { unit_price: p.unit_price, is_custom: p.is_custom }));
      });
      setPriceMap(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching client domain prices');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, slugs]);

  const applyClientCell = useCallback((slug: string, tld: string, cell: ClientDomainCell | null) => {
    setPriceMap((prev) => {
      const next = new Map(prev);
      if (cell === null) {
        next.delete(cellKey(slug, tld));
      } else {
        next.set(cellKey(slug, tld), cell);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (enabled) {
      void fetchAll();
    } else {
      setPriceMap(new Map());
    }
  }, [enabled, fetchAll]);

  return { priceMap, loading, error, refetch: fetchAll, applyClientCell };
};
